import { useState, useCallback, useEffect, useMemo, useRef } from "react";
import Admin from "../Admin/Admin.js";
import Header from "../Header/Header.js";
import RespondsTable from "../RespondsTable/RespondsTable.js";
import RespondSender from "../RespondsTable/RespondSender.js";
import { api } from "../../utils/api.js";
import { ENDPOINTS as E } from "../../utils/endpoints.js";
import {
  clearFilterWithDependencies,
  updateFilter,
  getSourceStats,
} from "../../utils/filters.js";
import { MINUTE } from "../../consts.js";
import "./App.css";

function App({ handleLogout, isAdmin, isModer }) {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [minDate, setMinDate] = useState(
    new Date().setDate(new Date().getDate() - 1)
  );
  const [maxDate, setMaxDate] = useState(new Date());
  const [sortBy, setSortBy] = useState("ink");
  const [sortOrder, setSortOrder] = useState("asc");
  const [filters, setFilters] = useState({
    ink: [],
    company: [],
    vacancy: [],
    manager: [],
  });
  const [showFilterModal, setShowFilterModal] = useState(null);
  const [showDaily, setShowDaily] = useState(false);
  const [showResponds, setShowResponds] = useState(false);
  const [delay, setDelay] = useState(5 * MINUTE);
  const [isDelayChanged, setDelayChanged] = useState(false);
  const [autoTurn, setAutoTurn] = useState(false);

  const lastRequestId = useRef(0);

  const getIntervals = useCallback((startDate, endDate, daily = false) => {
    if (daily) {
      const intervals = [];
      let currentDate = new Date(startDate);
      const end = new Date(endDate);

      while (currentDate <= end) {
        const dayStart = new Date(currentDate);
        const dayEnd = new Date(currentDate);
        dayEnd.setHours(23, 59, 59, 999);

        intervals.push({
          start: dayStart,
          end: dayEnd,
          label: dayStart.toLocaleDateString("ru-RU"),
          isDaily: true,
        });

        currentDate.setDate(currentDate.getDate() + 1);
      }

      return intervals;
    } else {
      const intervals = [];
      let currentStart = new Date(startDate);
      const end = new Date(endDate);

      const firstMonday = new Date(currentStart);
      const dayOfWeek = firstMonday.getDay();
      const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      firstMonday.setDate(firstMonday.getDate() - daysToSubtract);

      let weekStart = new Date(firstMonday);

      while (weekStart <= end) {
        let weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);

        if (weekEnd > end) {
          weekEnd = new Date(end);
        }

        const actualStart =
          weekStart < new Date(startDate) ? new Date(startDate) : weekStart;

        if (actualStart <= weekEnd) {
          intervals.push({
            start: actualStart,
            end: weekEnd,
            label: `с ${actualStart.toLocaleDateString(
              "ru-RU"
            )} по ${weekEnd.toLocaleDateString("ru-RU")}`,
            isDaily: false,
          });
        }

        weekStart = new Date(weekEnd);
        weekStart.setDate(weekStart.getDate() + 1);
      }

      return intervals;
    }
  }, []);

  const intervals = useMemo(() => {
    const result = getIntervals(minDate, maxDate, showDaily);
    return result || [];
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minDate, maxDate, showDaily]);

  const validateMaxDate = useCallback((value) => {
    const val = new Date(value);
    const now = new Date();
    if (val > now) {
      setMaxDate(now);
    } else {
      setMaxDate(val);
    }
  }, []);

  const resetTimeToStartOfDay = useCallback((date) => {
    const newDate = new Date(date);
    newDate.setHours(0, 0, 0, 0);
    return newDate.getTime();
  }, []);

  useEffect(() => {
    if (isAdmin) {
      setMinDate((prev) => resetTimeToStartOfDay(prev));
    }
  }, [isAdmin, resetTimeToStartOfDay]);

  const groupByKey = useCallback((arr, key) => {
    return arr.reduce((map, obj) => {
      const groupKey = obj[key];
      const group = map.get(groupKey) || [];
      group.push(obj);
      map.set(groupKey, group);
      return map;
    }, new Map());
  }, []);

  const isTimestampInInterval = (timestamp, intervalStart, intervalEnd) => {
    const respondDate = new Date(timestamp);
    const intervalStartStartOfDay = new Date(intervalStart);
    intervalStartStartOfDay.setHours(0, 0, 0, 0);
    const intervalEndEndOfDay = new Date(intervalEnd);
    intervalEndEndOfDay.setHours(23, 59, 59, 999);
    return (
      respondDate >= intervalStartStartOfDay &&
      respondDate <= intervalEndEndOfDay
    );
  };

  async function refreshUsers() {
    const role = isAdmin ? 4 : isModer ? 3 : 2;
    const us = await api.get(E.GET_USERS, role);

    if (us.success && us.data.length) {
      setUsers(us.data || []);
    }
  }

  const getData = useCallback(async () => {
    const requestId = ++lastRequestId.current;

    try {
      const start = new Date(minDate);
      const end = new Date(maxDate);

      if (!isAdmin) {
        start.setHours(3, 0, 0, 0);
        end.setHours(2, 59, 59, 999);
      } else {
        start.setHours(start.getHours() + 3);
        end.setHours(end.getHours() + 3);
      }

      const role = isAdmin ? 4 : isModer ? 3 : 2;
      const dates = [start.toISOString(), end.toISOString()];
      const resp = await api.post(E.GET_DATA, role, { dates });

      if (requestId !== lastRequestId.current) {
        return;
      }

      if (isAdmin) {
        const us = await api.get(E.GET_USERS, role);
        if (us.success && us.data.length) {
          setUsers(us.data || []);
        }
      }

      if (resp.success && resp.data) {
        const groupByInk = groupByKey(resp.data, "ink");

        const result = Array.from(groupByInk.keys()).map((ink) => {
          const inkResponds = groupByInk.get(ink);
          const company = inkResponds[0]?.company || "Неизвестно";
          const groupByVacancy = groupByKey(inkResponds, "vacancy");

          const vacs = Array.from(groupByVacancy.keys()).map((vacancy) => {
            const vacancyResponds = groupByVacancy.get(vacancy);

            const intervalCounts = intervals.map((interval) => {
              if (!vacancyResponds) return 0;
              const count = vacancyResponds.filter((respond) => {
                return (
                  respond &&
                  isTimestampInInterval(
                    respond.ts,
                    interval.start,
                    interval.end
                  )
                );
              }).length;
              return count;
            });

            const intervalSources = isAdmin
              ? intervals.map((interval) =>
                  getSourceStats(vacancyResponds, interval.start, interval.end)
                )
              : [];

            return {
              vacancy,
              manager: vacancyResponds[0]?.manager || "не указан",
              responds: vacancyResponds || [],
              respondCount: vacancyResponds?.length || 0,
              intervalCounts: intervalCounts || [],
              intervalSources: intervalSources || [],
            };
          });

          return {
            ink,
            company,
            vacs: vacs || [],
            totalResponds: vacs.reduce(
              (sum, vac) => sum + (vac?.respondCount || 0),
              0
            ),
          };
        });

      Promise.resolve().then(() => {
      setData(result || []);
    });
      } else {
        setData([]);
      }
    } catch (err) {
      if (requestId !== lastRequestId.current) return;
      console.error("Error fetching data:", err.message);
      setData([]);
    }
  }, [minDate, maxDate, isAdmin, isModer, groupByKey, intervals]);

  const handleSort = useCallback(
    (newSortBy) => {
      if (newSortBy === sortBy) {
        setSortOrder((current) => (current === "asc" ? "desc" : "asc"));
      } else {
        setSortBy(newSortBy);
        setSortOrder("desc");
      }
    },
    [sortBy]
  );

  const handleFilterChange = useCallback((filterType, value) => {
    setFilters((prev) => updateFilter(prev, filterType, value));
  }, []);

  const clearFilter = useCallback((filterType) => {
    setFilters((prev) => clearFilterWithDependencies(prev, filterType));
  }, []);

  useEffect(() => {
    getData();
  }, [getData]);

  return (
    <main>
      <Header
        isAdmin={isAdmin}
        autoTurn={autoTurn}
        setAutoTurn={setAutoTurn}
        minDate={minDate}
        maxDate={maxDate}
        setMinDate={setMinDate}
        validateMaxDate={validateMaxDate}
        showDaily={showDaily}
        setShowDaily={setShowDaily}
        clearFilter={clearFilter}
        filters={filters}
        handleLogout={handleLogout}
        isModer={isModer}
        showResponds={showResponds}
        setShowResponds={setShowResponds}
        setDelay={setDelay}
        setDelayChanged={setDelayChanged}
      />
      {!isModer || !showResponds ? (
        <RespondsTable
          isAdmin={isAdmin}
          data={data}
          intervals={intervals}
          showDaily={showDaily}
          onSort={handleSort}
          onFilterChange={handleFilterChange}
          onClearFilter={clearFilter}
          filters={filters}
          sortBy={sortBy}
          sortOrder={sortOrder}
          showFilterModal={showFilterModal}
          setShowFilterModal={setShowFilterModal}
          setFilters={setFilters}
        />
      ) : (
        <RespondSender
          delay={delay}
          isDelayChanged={isDelayChanged}
          setDelayChanged={setDelayChanged}
          isAdmin={isAdmin}
          isModer={isModer}
          autoTurn={autoTurn}
        />
      )}

      {isAdmin ? (
        <Admin users={users} setUsers={setUsers} refreshUsers={refreshUsers} handleLogout={handleLogout} />
      ) : (
        ""
      )}
    </main>
  );
}

export default App;
